import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Iconify from '../../../components/Iconify';
import { authApiCall } from '../../../services/http';
import { endpoints } from '../../../config';
import { setAuthToken, setAuthUser } from '../../../redux/reducers/authSlice';
import { productLabel } from '../../../helpers/products';
import ProductFormDialog from './ProductFormDialog';

const STATUS_COLOR = { active: 'default', deprecated: 'warning', archived: 'default' };

ProductsManagement.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default function ProductsManagement({ open, onClose }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authToken = useSelector((state) => state.auth.authToken);

  const apiProps = { navigate, dispatch, setAuthUser, setAuthToken, authToken, redirectRoute: null };

  const [products, setProducts] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authApiCall({
        method: 'GET',
        endpoint: endpoints.products,
        path: '/',
        ...apiProps,
      });
      const data = res?.data;
      const list = Array.isArray(data) ? data : data?.products || data?.items || [];
      setProducts(list);
      setWarnings(data?.audit?.warnings || []);
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Error cargando productos');
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const sortAlpha = (a, b) => productLabel(a).localeCompare(productLabel(b), 'es');
  const plans = products.filter((p) => p.type === 'plan').sort(sortAlpha);
  const addons = products.filter((p) => p.type !== 'plan').sort(sortAlpha);

  const openCreate = () => {
    setEditingProduct(null);
    setDialogOpen(true);
  };

  const openEdit = (product) => {
    setEditingProduct(product);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingProduct(null);
  };

  const onSaved = () => {
    closeDialog();
    load();
  };

  const renderTable = (items) => (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Slug</TableCell>
            <TableCell>Nombre</TableCell>
            <TableCell>Estado</TableCell>
            <TableCell>Capabilities</TableCell>
            <TableCell>Limits</TableCell>
            <TableCell align="right" />
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((p) => (
            <TableRow key={p.slug} hover sx={{ cursor: 'pointer' }} onClick={() => openEdit(p)}>
              <TableCell>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                  {p.slug}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">{productLabel(p)}</Typography>
                {p.comments && (
                  <Tooltip title={p.comments}>
                    <Stack direction="row" spacing={0.5} alignItems="flex-start" sx={{ mt: 0.25, maxWidth: 260 }}>
                      <Iconify
                        icon="eva:message-square-outline"
                        width={14}
                        sx={{ color: 'text.disabled', mt: '2px', flexShrink: 0 }}
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {p.comments}
                      </Typography>
                    </Stack>
                  </Tooltip>
                )}
              </TableCell>
              <TableCell>
                <Chip label={p.status} size="small" color={STATUS_COLOR[p.status] || 'default'} />
              </TableCell>
              <TableCell>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {(p.capabilities || []).map((c) => (
                    <Chip key={c} label={c} size="small" variant="outlined" sx={{ fontSize: 11 }} />
                  ))}
                  {(!p.capabilities || p.capabilities.length === 0) && (
                    <Typography variant="caption" color="text.disabled">
                      —
                    </Typography>
                  )}
                </Stack>
              </TableCell>
              <TableCell>
                {p.limits && Object.keys(p.limits).length > 0 ? (
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {Object.entries(p.limits).map(([k, v]) => (
                      <Chip key={k} label={`${k}: ${v}`} size="small" variant="outlined" sx={{ fontSize: 11 }} />
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="caption" color="text.disabled">
                    —
                  </Typography>
                )}
              </TableCell>
              <TableCell align="right">
                <Tooltip title="Editar">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      openEdit(p);
                    }}
                  >
                    <Iconify icon="eva:edit-2-outline" width={18} />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
          {items.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Sin productos.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Catálogo de productos</Typography>
          <Button variant="contained" size="small" startIcon={<Iconify icon="eva:plus-fill" />} onClick={openCreate}>
            Crear producto
          </Button>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={32} />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {warnings.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Catálogo desincronizado
            </Typography>
            <Stack component="ul" spacing={0.25} sx={{ m: 0, pl: 2 }}>
              {warnings.map((w, i) => (
                <Typography key={i} component="li" variant="caption">
                  {typeof w === 'string' ? w : w?.message || JSON.stringify(w)}
                </Typography>
              ))}
            </Stack>
          </Alert>
        )}

        {!loading && (
          <>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                Planes
              </Typography>
              {renderTable(plans)}
            </Box>

            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                Add-ons
              </Typography>
              {renderTable(addons)}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>

      <ProductFormDialog
        open={dialogOpen}
        onClose={closeDialog}
        product={editingProduct}
        apiProps={apiProps}
        onSaved={onSaved}
      />
    </Dialog>
  );
}
